import React, { useState, useMemo } from "react";
import { Box, Typography, alpha, Chip } from "@mui/material";
import { Add as AddIcon, Restaurant as RestaurantIcon, ShoppingCart as CartIcon } from "@mui/icons-material";
import ImageLoader from "components/ImageLoader/ImageLoader";
import { formatCurrency } from "../../../utils/currencyUtils";
import { LUXURY } from "data/guestLuxuryTheme";

const MenuItemCard = ({ item, onAddToCart, onDirectAdd, luxury = false }) => {
  const hasVariations = item.ItemVariations && item.ItemVariations.length > 0;
  const hasModifiers = item.ModifierOptions && item.ModifierOptions.length > 0;

  const sortedVariations = useMemo(() => {
    if (!hasVariations) return [];
    return [...item.ItemVariations].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  }, [item.ItemVariations, hasVariations]);

  const [selectedVariation, setSelectedVariation] = useState(
    () => (sortedVariations.length > 0 ? sortedVariations[0] : null)
  );

  const currentPrice = useMemo(() => {
    if (selectedVariation) {
      return parseFloat(String(selectedVariation.price || "0").replace(/[^0-9.-]/g, "")) || 0;
    }
    return parseFloat(String(item.basePrice || "0").replace(/[^0-9.-]/g, "")) || 0;
  }, [selectedVariation, item.basePrice]);

  const handleAdd = (e) => {
    e.stopPropagation();
    onAddToCart(item);
  };

  const handleCustomize = (e) => {
    e.stopPropagation();
    onAddToCart(item);
  };

  return (
    <Box
      onClick={handleAdd}
      sx={{
        width: "100%",
        bgcolor: luxury ? LUXURY.card : "#ffffff",
        borderRadius: luxury ? "20px" : "14px",
        overflow: "hidden",
        boxShadow: luxury ? LUXURY.cardShadow : "0 2px 10px rgba(0,0,0,0.06)",
        border: luxury ? `1px solid ${LUXURY.cardBorder}` : "1px solid #efefef",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
        "@media (hover: hover)": {
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: luxury
              ? LUXURY.foodShadow
              : "0 8px 24px rgba(107,125,207,0.16)",
            borderColor: luxury ? LUXURY.gold : "#d4d9f5",
          },
          "&:hover .item-img": { transform: "scale(1.05)" },
        },
      }}
    >
      {/* ── Image ── */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "4 / 3",
          overflow: "hidden",
          bgcolor: luxury ? "#F3EDE3" : "#f5f6fa",
          flexShrink: 0,
        }}
      >
        {item.image ? (
          <Box
            className="item-img"
            sx={{ width: "100%", height: "100%", transition: "transform 0.35s ease" }}
          >
            <ImageLoader
              imagePath={item.image}
              width="100%"
              height="100%"
              alt={item.name}
              rounded={false}
              borderRadius={0}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: luxury ? "#F3EDE3" : "#f0f2fa",
            }}
          >
            <RestaurantIcon sx={{ fontSize: { xs: 36, md: 44 }, color: alpha(luxury ? LUXURY.orange : "#6b7dcf", 0.28) }} />
          </Box>
        )}

        {/* Veg / Non-veg dot */}
        {item.isVegetarian !== undefined && item.isVegetarian !== null && (
          <Box
            sx={{
              position: "absolute",
              top: 7,
              left: 7,
              width: 18,
              height: 18,
              borderRadius: "4px",
              border: `2px solid ${item.isVegetarian ? "#16a34a" : "#dc2626"}`,
              bgcolor: "rgba(255,255,255,0.95)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: item.isVegetarian ? "#16a34a" : "#dc2626",
              }}
            />
          </Box>
        )}

        {/* Customizable badge */}
        {(hasModifiers || hasVariations) && (
          <Box
            sx={{
              position: "absolute",
              bottom: 7,
              right: 7,
              bgcolor: luxury ? "rgba(18,56,44,0.88)" : "rgba(107,125,207,0.92)",
              color: luxury ? LUXURY.cream : "#fff",
              fontSize: "0.56rem",
              fontWeight: 800,
              letterSpacing: "0.04em",
              px: 0.9,
              py: 0.3,
              borderRadius: luxury ? "999px" : "5px",
              lineHeight: 1.4,
              backdropFilter: "blur(3px)",
            }}
          >
            CUSTOMISE
          </Box>
        )}
      </Box>

      {/* ── Info ── */}
      <Box
        sx={{
          p: "10px 10px 0 10px",
          display: "flex",
          flexDirection: "column",
          gap: 0.4,
          flexGrow: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: "0.82rem", md: "0.875rem" },
            color: luxury ? LUXURY.ink : "#1e293b",
            letterSpacing: luxury ? "-0.02em" : 0,
            lineHeight: 1.35,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {item.name}
        </Typography>

        {item.description && (
          <Typography
            sx={{
              fontSize: { xs: "0.68rem", md: "0.7rem" },
              color: luxury ? LUXURY.hint : "#94a3b8",
              lineHeight: 1.4,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {item.description}
          </Typography>
        )}

        {/* Variation pills */}
        {hasVariations && (
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              overflowX: "auto",
              pb: 0.25,
              mt: 0.2,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {sortedVariations.map((v) => {
              const isSelected = selectedVariation?.id === v.id;
              return (
                <Chip
                  key={v.id}
                  label={v.name}
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setSelectedVariation(v); }}
                  sx={{
                    height: 20,
                    fontSize: "0.6rem",
                    fontWeight: isSelected ? 700 : 500,
                    borderRadius: luxury ? "999px" : "5px",
                    flexShrink: 0,
                    cursor: "pointer",
                    bgcolor: isSelected
                      ? luxury ? LUXURY.forest : "#6b7dcf"
                      : luxury ? "#F3EDE3" : "#f0f2fa",
                    color: isSelected
                      ? luxury ? LUXURY.cream : "#fff"
                      : luxury ? LUXURY.muted : "#6b7280",
                    border: `1px solid ${isSelected
                      ? luxury ? LUXURY.forest : "#6b7dcf"
                      : luxury ? LUXURY.cardBorder : "#e5e7eb"}`,
                    "& .MuiChip-label": { px: 0.65 },
                    transition: "all 0.12s ease",
                  }}
                />
              );
            })}
          </Box>
        )}
      </Box>

      {/* ── Price + Add row ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "10px",
          pt: 0.75,
          pb: "10px",
          mt: "auto",
          gap: 0.5,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: { xs: "0.88rem", md: "0.9rem" },
            color: luxury ? LUXURY.forest : "#4f5fa8",
            lineHeight: 1,
          }}
        >
          {formatCurrency(currentPrice, "Rs.", { space: true })}
        </Typography>

        <Box
          component="button"
          onClick={handleAdd}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.4,
            border: "none",
            cursor: "pointer",
            borderRadius: luxury ? "999px" : "8px",
            background: luxury
              ? LUXURY.ctaGradient
              : "linear-gradient(135deg, #6b7dcf 0%, #5568d3 100%)",
            color: luxury ? LUXURY.forestDeep : "#fff",
            boxShadow: luxury ? LUXURY.ctaShadow : "0 2px 7px rgba(85,104,211,0.32)",
            height: { xs: 30, md: 27 },
            px: { xs: 1.1, md: 1.2 },
            transition: "all 0.15s ease",
            WebkitTapHighlightColor: "transparent",
            "@media (hover: hover)": {
              "&:hover": {
                background: luxury
                  ? LUXURY.ctaGradientHover
                  : "linear-gradient(135deg, #5568d3 0%, #4455c0 100%)",
                boxShadow: luxury ? LUXURY.ctaShadowHover : "0 4px 12px rgba(85,104,211,0.4)",
                transform: "scale(1.04)",
              },
            },
            "&:active": { transform: "scale(0.95)" },
          }}
        >
          <AddIcon sx={{ fontSize: 14 }} />
          <CartIcon sx={{ fontSize: { xs: 14, md: 13 } }} />
        </Box>
      </Box>
    </Box>
  );
};

export default MenuItemCard;

